import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Users, UserX, UserCheck, RefreshCw, ChevronDown, ChevronUp, Save, Loader2, UserPlus, MailWarning, MailCheck, X, Crown, Briefcase, AlertTriangle, Clock } from "lucide-react";
import { API, authHeaders, ROLE_BADGE_STYLES, QUALITY_STATUSES, JOB_STATUS_LABELS } from "@/constants/roles";
import { AdminWorkerProfile } from "@/components/admin/AdminWorkerProfile";
import { usePerms } from "@/components/PermsContext";
import { fmtMoney, fmtNum } from "@/utils/currency";

const INVITE_ROLES = ["Standard Animator", "High-Quality Animator", "Script Writer", "Video Editor", "Production Manager"];
const EMPTY_INVITE = { name: "", email: "", role: "Standard Animator", pay_rate: "", notes: "" };

const inputCls =
  "w-full rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-bg)] px-4 py-3 text-sm font-medium text-[var(--pv-text)] placeholder:text-[var(--pv-text-4)] focus:border-cyan-400 focus:outline-none focus:ring-4 focus:ring-cyan-500/10 transition-all";

const wlStatus = (wl) => {
  if (!wl || wl.active_count === 0) return { label: "Available", cls: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" };
  if (wl.overdue_count > 0) return { label: "Overdue", cls: "border-rose-500/30 bg-rose-500/10 text-rose-300" };
  if (wl.due_soon_count > 0) return { label: "Due soon", cls: "border-amber-500/30 bg-amber-500/10 text-amber-300" };
  return { label: "Working", cls: "border-cyan-500/30 bg-cyan-500/10 text-cyan-300" };
};

export const AdminTeam = ({ onOpenVideo }) => {
  const { role } = usePerms();
  const [team, setTeam] = useState(null);
  const [profileId, setProfileId] = useState(null);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("All");
  const [showArchived, setShowArchived] = useState(false);
  const [invites, setInvites] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState(EMPTY_INVITE);
  const [inviting, setInviting] = useState(false);
  const [inviteBusy, setInviteBusy] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [notesDraft, setNotesDraft] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [workload, setWorkload] = useState({}); // animatorId -> live workload (from jobs collection)
  const [workloadFor, setWorkloadFor] = useState(null); // animator id whose active-jobs modal is open
  const navigate = useNavigate();

  const load = useCallback(() => {
    axios
      .get(`${API}/admin/team${showArchived ? "?include_archived=true" : ""}`, { headers: authHeaders() })
      .then((r) => setTeam(r.data))
      .catch((err) => {
        if (err.response?.status === 401 || err.response?.status === 403) navigate("/login");
        else toast.error("Could not load team.");
      });
    axios
      .get(`${API}/admin/invites`, { headers: authHeaders() })
      .then((r) => setInvites(r.data))
      .catch(() => {});
    // live workload per animator — same view powers the job-creation picker
    axios
      .get(`${API}/admin/animator-workload`, { headers: authHeaders() })
      .then((r) => setWorkload(Object.fromEntries((r.data.animators || []).map((a) => [a.id, a]))))
      .catch(() => {});
  }, [navigate, showArchived]);

  useEffect(load, [load]);

  const visibleTeam = (team || []).filter((w) => {
    if (roleFilter === "Top Animators") { if (!w.is_top_animator) return false; }
    else if (roleFilter !== "All") {
      // Animator tabs read the CURRENT authoritative eligibility (animator_roles), never the
      // legacy worker_role string — a promoted HQ animator leaves the Standard tab immediately.
      if (roleFilter === "Standard Animator" || roleFilter === "High-Quality Animator") {
        if (!(w.animator_roles || []).includes(roleFilter)) return false;
      } else if (w.worker_role !== roleFilter) return false;
    }
    const q = search.trim().toLowerCase();
    if (q && !((w.name || "") + " " + w.email + " " + (w.tags || []).join(" ")).toLowerCase().includes(q)) return false;
    return true;
  });

  if (profileId) {
    return (
      <AdminWorkerProfile
        workerId={profileId}
        onBack={() => setProfileId(null)}
        onChanged={load}
        onOpenVideo={onOpenVideo}
      />
    );
  }

  const sendInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/invites`,
        {
          name: inviteForm.name,
          email: inviteForm.email,
          role: inviteForm.role,
          pay_rate: inviteForm.pay_rate || null,
          notes: inviteForm.notes || null,
        },
        { headers: authHeaders() }
      );
      toast.success(data.email_sent ? `Invite emailed to ${data.email}.` : "Invite created — email failed to send, use Resend to retry.");
      setInviteForm(EMPTY_INVITE);
      setShowInvite(false);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not create invite.");
    } finally {
      setInviting(false);
    }
  };

  const resendInvite = async (id) => {
    setInviteBusy(id);
    try {
      const { data } = await axios.post(`${API}/admin/invites/${id}/resend`, {}, { headers: authHeaders() });
      toast.success(data.email_sent ? "Invite re-sent with a fresh link." : "Re-send failed at the email service — try again shortly.");
    } catch {
      toast.error("Could not resend invite.");
    } finally {
      setInviteBusy(null);
    }
  };

  const revokeInvite = async (id) => {
    setInviteBusy(id);
    try {
      await axios.delete(`${API}/admin/invites/${id}`, { headers: authHeaders() });
      toast.success("Invite revoked — the link no longer works.");
      load();
    } catch {
      toast.error("Could not revoke invite.");
    } finally {
      setInviteBusy(null);
    }
  };

  const toggleActive = async (w) => {
    try {
      await axios.patch(`${API}/admin/users/${w.id}/status`, { deactivated: !w.deactivated }, { headers: authHeaders() });
      toast.success(!w.deactivated ? "Production access disabled." : "Worker reactivated.");
      load();
    } catch {
      toast.error("Could not update worker access.");
    }
  };

  const setQuality = async (w, quality_status) => {
    try {
      await axios.patch(`${API}/admin/team/${w.id}`, { quality_status }, { headers: authHeaders() });
      setTeam((t) => t.map((x) => (x.id === w.id ? { ...x, quality_status } : x)));
      toast.success("Quality status updated.");
    } catch {
      toast.error("Could not update quality status.");
    }
  };

  const saveNotes = async (w) => {
    setSavingId(w.id);
    try {
      const admin_notes = notesDraft[w.id] ?? w.admin_notes ?? "";
      await axios.patch(`${API}/admin/team/${w.id}`, { admin_notes }, { headers: authHeaders() });
      setTeam((t) => t.map((x) => (x.id === w.id ? { ...x, admin_notes } : x)));
      toast.success("Notes saved.");
    } catch {
      toast.error("Could not save notes.");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div data-testid="admin-team">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-[var(--pv-text)]">Team</h1>
          <p className="mt-1 text-sm text-[var(--pv-text-4)]">Your crew — output, earnings, quality standing, and access control.</p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="team-refresh-btn"
            onClick={load}
            className="flex items-center gap-1.5 rounded-full border border-[var(--pv-border)] bg-[var(--pv-card)] px-3.5 py-2 text-xs font-semibold text-[var(--pv-text-4)] transition-colors hover:bg-[var(--pv-subtle)]"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
          {role === "admin" && (
            <button
              data-testid="invite-worker-btn"
              onClick={() => setShowInvite((s) => !s)}
              className="flex items-center gap-1.5 rounded-full bg-cyan-500 px-4 py-2 text-xs font-bold text-white shadow-md shadow-cyan-500/25 transition-colors hover:bg-cyan-600"
            >
              {showInvite ? <X className="h-3.5 w-3.5" /> : <UserPlus className="h-3.5 w-3.5" />}
              {showInvite ? "Close" : "Invite worker"}
            </button>
          )}
        </div>
      </div>

      {showInvite && role === "admin" && (
        <form data-testid="invite-form" onSubmit={sendInvite} className="mt-6 rounded-3xl border border-cyan-500/20 bg-[var(--pv-card)] p-6 sm:p-8">
          <h2 className="font-display text-lg font-bold text-[var(--pv-text)]">Invite someone directly</h2>
          <p className="mt-1 text-xs text-[var(--pv-text-4)]">
            For people you've already chosen — no public application. They get an email with a setup link, create their own password, and land in their dashboard as active crew. Pick "Production Manager" to invite a manager with the permissions you configure in Settings. If the email already has an applicant account, that same account is upgraded.
          </p>
          <div className="mt-5 grid gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="invite-name" className="mb-1.5 block text-xs font-semibold text-[var(--pv-text-2)]">Name / artist name</label>
              <input id="invite-name" data-testid="invite-name-input" required minLength={2} value={inviteForm.name} onChange={(e) => setInviteForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Alex 'Pixel' Rivera" className={inputCls} />
            </div>
            <div>
              <label htmlFor="invite-email" className="mb-1.5 block text-xs font-semibold text-[var(--pv-text-2)]">Email</label>
              <input id="invite-email" data-testid="invite-email-input" required type="email" value={inviteForm.email} onChange={(e) => setInviteForm((f) => ({ ...f, email: e.target.value }))} placeholder="them@example.com" className={inputCls} />
            </div>
            <div>
              <label htmlFor="invite-role" className="mb-1.5 block text-xs font-semibold text-[var(--pv-text-2)]">Role</label>
              <select id="invite-role" data-testid="invite-role-select" value={inviteForm.role} onChange={(e) => setInviteForm((f) => ({ ...f, role: e.target.value }))} className={inputCls}>
                {INVITE_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="invite-pay" className="mb-1.5 block text-xs font-semibold text-[var(--pv-text-2)]">Agreed pay rate (optional{inviteForm.role === "Production Manager" ? " — not used for managers" : ""})</label>
              <input id="invite-pay" data-testid="invite-pay-input" value={inviteForm.pay_rate} onChange={(e) => setInviteForm((f) => ({ ...f, pay_rate: e.target.value }))} placeholder="e.g. $80 per video" className={inputCls} disabled={inviteForm.role === "Production Manager"} />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="invite-notes" className="mb-1.5 block text-xs font-semibold text-[var(--pv-text-2)]">Private admin notes (optional — only you see this)</label>
              <textarea id="invite-notes" data-testid="invite-notes-input" rows={2} value={inviteForm.notes} onChange={(e) => setInviteForm((f) => ({ ...f, notes: e.target.value }))} placeholder="e.g. Great at action scenes, worked with them before..." className={inputCls} />
            </div>
          </div>
          <button
            data-testid="invite-send-btn"
            type="submit"
            disabled={inviting}
            className="mt-5 flex items-center gap-2 rounded-full bg-cyan-500 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-cyan-500/25 transition-colors hover:bg-cyan-600 disabled:opacity-60"
          >
            {inviting ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
            Send invite
          </button>
        </form>
      )}

      {invites.filter((i) => i.status === "pending").length > 0 && (
        <div className="mt-6 rounded-3xl border border-amber-500/20 bg-amber-500/5 p-6">
          <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-amber-400">
            <MailWarning className="h-4 w-4" /> Pending invites ({invites.filter((i) => i.status === "pending").length})
          </p>
          <div className="mt-4 space-y-2.5">
            {invites.filter((i) => i.status === "pending").map((inv) => (
              <div key={inv.id} data-testid={`invite-card-${inv.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-card)] px-4 py-3">
                <div>
                  <p className="flex flex-wrap items-center gap-2 text-sm font-bold text-[var(--pv-text)]">
                    {inv.name}
                    <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${ROLE_BADGE_STYLES[inv.role] || ""}`}>{inv.role}</span>
                  </p>
                  <p className="mt-0.5 text-xs text-[var(--pv-text-4)]">
                    {inv.email}
                    {inv.pay_rate && <span className="ml-2 font-semibold text-[var(--pv-text-2)]">{inv.pay_rate}</span>}
                    <span className="ml-2">· sent {new Date(inv.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    data-testid={`invite-resend-${inv.id}`}
                    onClick={() => resendInvite(inv.id)}
                    disabled={inviteBusy === inv.id}
                    className="flex items-center gap-1.5 rounded-full bg-cyan-500/15 border border-cyan-500/30 px-3.5 py-1.5 text-xs font-bold text-cyan-300 transition-colors hover:bg-cyan-500/25 disabled:opacity-60"
                  >
                    {inviteBusy === inv.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <MailCheck className="h-3 w-3" />}
                    Resend
                  </button>
                  <button
                    data-testid={`invite-revoke-${inv.id}`}
                    onClick={() => revokeInvite(inv.id)}
                    disabled={inviteBusy === inv.id}
                    className="rounded-full bg-rose-500/10 px-3.5 py-1.5 text-xs font-bold text-rose-400 transition-colors hover:bg-rose-500/20 disabled:opacity-60"
                  >
                    Revoke
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap gap-2">
          {["All", "Standard Animator", "High-Quality Animator", "Script Writer", "Video Editor", "Top Animators"].map((r) => (
            <button
              key={r}
              data-testid={`team-filter-${r.toLowerCase().replace(/[\s/-]+/g, "-")}`}
              onClick={() => setRoleFilter(r)}
              className={`flex items-center gap-1 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${
                roleFilter === r ? (r === "Top Animators" ? "bg-amber-400 text-amber-950" : "bg-cyan-500 text-white") : "border border-[var(--pv-border)] bg-[var(--pv-card)] text-[var(--pv-text-3)] hover:border-cyan-500/30"
              }`}
            >
              {r === "Top Animators" && <Crown className="h-3 w-3" />}
              {r}
            </button>
          ))}
          <button
            data-testid="team-archived-toggle"
            onClick={() => setShowArchived((s) => !s)}
            className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${
              showArchived ? "bg-amber-500/15 border border-amber-500/30 text-amber-300" : "border border-[var(--pv-border)] bg-[var(--pv-card)] text-[var(--pv-text-4)]"
            }`}
          >
            {showArchived ? "Including archived" : "Show archived"}
          </button>
        </div>
        <input
          data-testid="team-search-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, email, tag..."
          className="w-full rounded-full border border-[var(--pv-border)] bg-[var(--pv-card)] px-4 py-2.5 text-sm font-medium text-[var(--pv-text)] placeholder:text-[var(--pv-text-4)] focus:border-cyan-400 focus:outline-none lg:w-64"
        />
      </div>

      <div className="mt-6 space-y-4">
        {!team ? (
          <p data-testid="team-loading" className="py-16 text-center text-sm font-medium text-[var(--pv-text-4)]">Loading team...</p>
        ) : visibleTeam.length === 0 ? (
          <div data-testid="team-empty" className="flex flex-col items-center gap-3 rounded-3xl border border-dashed border-[var(--pv-border-strong)] bg-[var(--pv-card)] py-16">
            <Users className="h-10 w-10 text-[var(--pv-text-2)]" />
            <p className="text-sm font-medium text-[var(--pv-text-4)]">No workers match — accept an application or invite someone directly.</p>
          </div>
        ) : (
          visibleTeam.map((w) => {
            const qs = QUALITY_STATUSES[w.quality_status] || QUALITY_STATUSES.good_standing;
            return (
              <article key={w.id} data-testid={`team-card-${w.id}`} className="rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-6 ">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex flex-wrap items-center gap-2.5">
                      <h3 className="font-display text-lg font-semibold text-[var(--pv-text)]">{w.name || w.email}</h3>
                      {w.is_top_animator && (
                        <span data-testid={`top-animator-badge-${w.id}`} className="flex items-center gap-1 rounded-full border border-amber-400/40 bg-amber-400/10 px-2.5 py-0.5 text-[11px] font-bold text-amber-300">
                          <Crown className="h-3 w-3" /> Top Animator
                        </span>
                      )}
                      {(w.display_role || w.worker_role) && (
                        <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-bold ${ROLE_BADGE_STYLES[w.display_role] || ROLE_BADGE_STYLES[w.worker_role] || ROLE_BADGE_STYLES["General / Multiple Roles"]}`}>{w.display_role || w.worker_role}</span>
                      )}
                      <span data-testid={`quality-badge-${w.id}`} className={`rounded-full border px-2.5 py-0.5 text-[11px] font-bold ${qs.style}`}>{qs.label}</span>
                      {w.deactivated && (
                        <span data-testid={`deactivated-badge-${w.id}`} className="rounded-full border border-rose-500/30 bg-rose-500/15 px-2.5 py-0.5 text-[11px] font-bold uppercase text-rose-300">Access disabled</span>
                      )}
                      {w.archived && (
                        <span className="rounded-full border border-[var(--pv-border)] bg-[var(--pv-raised)] px-2.5 py-0.5 text-[11px] font-bold uppercase text-[var(--pv-text-3)]">Archived</span>
                      )}
                    </div>
                    <p className="mt-1.5 text-xs font-medium text-[var(--pv-text-4)]">
                      {w.email}
                      {w.pay_rate && (
                        <>
                          <span className="mx-2 text-[var(--pv-border-strong)]">|</span>
                          <span className="font-semibold text-[var(--pv-text-2)]">{w.pay_rate}</span>
                        </>
                      )}
                      {w.hire_date && (
                        <>
                          <span className="mx-2 text-[var(--pv-text-2)]">|</span>
                          Crew since {new Date(w.hire_date).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                        </>
                      )}
                    </p>
                    {w.payment_status && (
                      <span
                        data-testid={`team-payment-status-${w.id}`}
                        className={`mt-1.5 inline-flex w-fit items-center gap-1 rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase ${
                          w.payment_status === "complete"
                            ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                            : w.payment_status === "pending"
                              ? "border-amber-500/30 bg-amber-500/10 text-amber-300"
                              : w.payment_status === "partial"
                                ? "border-amber-500/30 bg-amber-500/10 text-amber-300"
                                : "border-rose-500/30 bg-rose-500/10 text-rose-300"
                        }`}
                      >
                        Payment: {w.payment_status === "complete" ? "Complete" : w.payment_status === "pending" ? "Pending approval" : w.payment_status === "partial" ? "Partial" : "Missing"}
                      </span>
                    )}
                    {/* live workload — click through to the animator's current jobs */}
                    {(w.animator_roles || []).length > 0 && workload[w.id] && (() => {
                      const wl = workload[w.id];
                      const st = wlStatus(wl);
                      return (
                        <button
                          data-testid={`workload-chip-${w.id}`}
                          onClick={() => setWorkloadFor(w.id)}
                          title="See this animator's active jobs"
                          className={`mt-1.5 ml-2 inline-flex w-fit items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase transition-transform hover:scale-[1.03] ${st.cls}`}
                        >
                          <Briefcase className="h-3 w-3" />
                          {wl.active_count} active · {st.label}
                          {wl.earliest_deadline && wl.active_count > 0 && (
                            <span className="normal-case opacity-80">· next {new Date(wl.earliest_deadline + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
                          )}
                        </button>
                      );
                    })()}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <select
                      data-testid={`quality-select-${w.id}`}
                      value={w.quality_status}
                      onChange={(e) => setQuality(w, e.target.value)}
                      className="rounded-full border border-[var(--pv-border)] bg-[var(--pv-card)] px-3.5 py-1.5 text-xs font-semibold text-[var(--pv-text-2)] focus:border-cyan-400 focus:outline-none"
                    >
                      {Object.entries(QUALITY_STATUSES).map(([k, v]) => (
                        <option key={k} value={k}>{v.label}</option>
                      ))}
                    </select>
                    <button
                      data-testid={`team-toggle-${w.id}`}
                      onClick={() => toggleActive(w)}
                      className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${
                        w.deactivated ? "bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/15" : "bg-rose-500/10 text-rose-400 hover:bg-rose-500/15"
                      }`}
                    >
                      {w.deactivated ? <UserCheck className="h-3.5 w-3.5" /> : <UserX className="h-3.5 w-3.5" />}
                      {w.deactivated ? "Reactivate" : "Deactivate"}
                    </button>
                    <button
                      data-testid={`team-profile-${w.id}`}
                      onClick={() => setProfileId(w.id)}
                      className="flex items-center gap-1 rounded-full bg-cyan-500 px-3.5 py-1.5 text-xs font-bold text-white transition-colors hover:bg-cyan-600"
                    >
                      <ChevronDown className="h-3.5 w-3.5 -rotate-90" />
                      Profile
                    </button>
                  </div>
                </div>

                {(w.tags || []).length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {w.tags.map((t) => (
                      <span key={t} className="rounded-full border border-cyan-500/30 bg-cyan-500/10 px-2.5 py-0.5 text-[10px] font-bold text-cyan-300">{t}</span>
                    ))}
                  </div>
                )}

                <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-5">
                  {[
                    { label: "Active jobs", value: w.active_jobs, testid: `team-active-${w.id}` },
                    { label: "Completed", value: w.completed_jobs, testid: `team-completed-${w.id}` },
                    { label: "Revisions", value: w.revisions, testid: `team-revisions-${w.id}` },
                    ...(w.total_paid != null ? [{ label: "Total paid", value: fmtMoney(w.total_paid), testid: `team-paid-${w.id}` }] : []),
                    ...(w.amount_owed != null ? [{ label: "Owed", value: fmtMoney(w.amount_owed), testid: `team-owed-${w.id}` }] : []),
                  ].map((s) => (
                    <div key={s.label} data-testid={s.testid} className="rounded-2xl bg-[var(--pv-subtle)] px-4 py-3">
                      <p className="font-display text-lg font-bold text-[var(--pv-text)]">{s.value}</p>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-[var(--pv-text-4)]">{s.label}</p>
                    </div>
                  ))}
                </div>
              </article>
            );
          })
        )}
      </div>

      {/* Active-jobs modal — the clickable workload chip's target. Pure read view over jobs. */}
      {workloadFor && workload[workloadFor] && (
        <div data-testid="workload-modal" className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm" onClick={() => setWorkloadFor(null)}>
          <div className="w-full max-w-lg rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-bg)] p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-display text-lg font-bold text-[var(--pv-text)]">
                {workload[workloadFor].name} — {workload[workloadFor].active_count} active job{workload[workloadFor].active_count === 1 ? "" : "s"}
              </h3>
              <button data-testid="workload-modal-close" onClick={() => setWorkloadFor(null)} className="rounded-full p-2 text-[var(--pv-text-4)] hover:bg-[var(--pv-subtle)]"><X className="h-4 w-4" /></button>
            </div>
            <div className="mt-4 max-h-96 space-y-2 overflow-y-auto">
              {workload[workloadFor].active_jobs.length === 0 ? (
                <p data-testid="workload-modal-empty" className="rounded-2xl border border-dashed border-[var(--pv-border-strong)] py-10 text-center text-xs text-[var(--pv-text-4)]">
                  No active jobs — this animator is available.
                </p>
              ) : (
                workload[workloadFor].active_jobs.map((j) => (
                  <div key={j.id} data-testid={`workload-job-${j.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-card)] px-4 py-3">
                    <div className="min-w-0">
                      <p className="truncate text-xs font-bold text-[var(--pv-text)]">{j.title}</p>
                      <p className="mt-0.5 text-[10px] font-medium text-[var(--pv-text-4)]">
                        {(JOB_STATUS_LABELS[j.status] || j.status)}
                        {j.deadline && <> · due {new Date(j.deadline + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" })}</>}
                      </p>
                    </div>
                    {j.overdue ? (
                      <span className="flex items-center gap-1 rounded-full border border-rose-500/30 bg-rose-500/10 px-2 py-0.5 text-[10px] font-bold uppercase text-rose-300"><AlertTriangle className="h-3 w-3" /> Overdue</span>
                    ) : j.due_soon ? (
                      <span className="flex items-center gap-1 rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-300"><Clock className="h-3 w-3" /> Due soon</span>
                    ) : null}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
